import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { IMAGES } from "@/data/spa";
import { useLang } from "@/i18n/LanguageContext";
import { TiltCard } from "@/components/spa/TiltCard";

const shots = [
  { src: IMAGES.hotStone, alt: "Hot stone massage", cls: "col-span-2 row-span-2" },
  { src: IMAGES.sauna, alt: "Sauna environment", cls: "col-span-1 row-span-1" },
  { src: IMAGES.zenStones, alt: "Zen stones", cls: "col-span-1 row-span-1" },
  { src: IMAGES.therapist, alt: "Massage therapist", cls: "col-span-1 row-span-1" },
  { src: IMAGES.steamRoom, alt: "Steam room", cls: "col-span-1 row-span-1" },
];

export const Atmosphere = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const textY = useTransform(scrollYProgress, [0, 1], [80, -80]);

  return (
    <section ref={ref} id="atmosphere" data-testid="atmosphere-section" className="relative py-24 lg:py-40 bg-[#0c100e] overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14 items-center">
        <motion.div style={{ y: textY }} className="lg:col-span-4">
          <div className="flex items-center gap-4 mb-6">
            <span className="h-px w-12 bg-[#c5a059]" />
            <span className="text-[11px] tracking-[0.4em] uppercase text-[#c5a059]">{t.atmosphere.label}</span>
          </div>
          <h2 className="font-serif-display font-medium text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight text-[#f0f4f2]">
            {t.atmosphere.title.pre}<span className="italic text-[#c5a059]">{t.atmosphere.title.it}</span>{t.atmosphere.title.post}
          </h2>
          <p className="mt-8 text-sm text-[#8a9690] font-light leading-relaxed max-w-sm">
            {t.atmosphere.body}
          </p>
        </motion.div>

        <div className="lg:col-span-8 grid grid-cols-4 grid-rows-2 gap-3 h-[420px] sm:h-[540px]">
          {shots.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 40, scale: 0.96 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              className={s.cls}
            >
              <TiltCard
                max={4}
                lift={5}
                glow="0 22px 55px -18px rgba(197,160,89,0.45)"
                className="group relative overflow-hidden w-full h-full"
              >
                <img
                  src={s.src}
                  alt={s.alt}
                  className="w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-[#050706]/30 group-hover:bg-transparent transition-colors duration-700" />
              </TiltCard>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
